import React, { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Spin } from 'antd';

import { PropTypes } from 'prop-types';

import BoardColumn from './BoardColumn';

import {
  openCardDrawer,
  openFormMapDrawer,
  openShortListDrawer,
} from '../state/boards.actions';

import { getProjectIdMap, getEquipmentIdMap } from '../../state/selectors';

export default function BoardView({
  cardTypeId,
  isLocked,
  showCardNumber,
  useCardNumberAsCardTitle,
  userBoardPermissions,
  loading,
  onBoardSelected,
}) {
  const dispatch = useDispatch();

  const boards = useSelector((state) => state.boards.boards);
  const selectedBoard = useSelector((state) => state.boards.selectedBoard);
  const {
    [cardTypeId]: cardTemplate = {},
  } = useSelector((state) => state.boards.cardTemplates);
  const isFullScreen = useSelector((state) => state.main.menuCollapsed || state.main.fullscreen);
  const fullCards = useSelector((state) => state.boards.fullCards);
  const dragSourceId = useSelector((state) => state.boards.dragSourceId);

  // Use globally memoized selectors - prevents memory duplication
  const projectIdMap = useSelector(getProjectIdMap);
  const equipmentIdMap = useSelector(getEquipmentIdMap);

  const {
    statuses = [],
  } = selectedBoard || {};

  const fullBoard = useMemo(() => (
    selectedBoard ? boards[selectedBoard.id] : {}
  ), [selectedBoard, boards]);

  const onAddCardWithStatus = useCallback((statusId) => {
    dispatch(openCardDrawer({
      statusId,
    }));
  }, [dispatch]);

  const onForm = useCallback((cardId) => {
    const {
      [cardId]: newSelectedCard,
    } = fullCards;
    if (!newSelectedCard) return;
    dispatch(openFormMapDrawer({
      selectedCardId: cardId,
    }));
  }, [dispatch, fullCards]);

  const onShortList = useCallback((cardId) => {
    const {
      [cardId]: newSelectedCard,
    } = fullCards;
    if (!newSelectedCard) return;
    dispatch(openShortListDrawer({
      selectedCardId: cardId,
    }));
  }, [dispatch, fullCards]);

  const onClick = useCallback((cardId) => {
    const {
      [cardId]: newSelectedCard,
    } = fullCards;
    if (!newSelectedCard) return;
    dispatch(openCardDrawer({
      selectedCardId: cardId,
      onForm,
      onShortList,
    }));
  }, [dispatch, fullCards, onForm, onShortList]);

  const onEdit = useCallback((cardId) => {
    const {
      [cardId]: newSelectedCard,
    } = fullCards;
    if (!newSelectedCard) return;
    dispatch(openCardDrawer({
      selectedCardId: cardId,
      editMode: true,
      onForm,
      onShortList,
    }));
  }, [dispatch, fullCards, onForm, onShortList]);

  const columns = useMemo(() => {
    const ourColumns = [...statuses];
    ourColumns.sort((a, b) => a.orderIndex - b.orderIndex);
    return ourColumns.map((status, index) => (
      <BoardColumn
        key={status.id}
        isLocked={isLocked}
        showCardNumber={useCardNumberAsCardTitle ? false : showCardNumber}
        {...status}
        onAdd={onAddCardWithStatus}
        onClick={onClick}
        onEdit={onEdit}
        onForm={onForm}
        onShortList={onShortList}
        cardTemplate={cardTemplate}
        userBoardPermissions={userBoardPermissions}
        projectIdMap={projectIdMap}
        equipmentIdMap={equipmentIdMap}
        onBoardSelected={onBoardSelected}
        boardSortType={fullBoard?.sortType}
        isDroppable={fullBoard?.sortType === 'default' || (fullBoard?.sortType !== 'default' && (index + 1) !== dragSourceId)}
      />
    ));
  }, [
    statuses,
    isLocked,
    onAddCardWithStatus,
    onClick,
    onEdit,
    cardTemplate,
    userBoardPermissions,
    projectIdMap,
    onBoardSelected,
    fullBoard,
    dragSourceId,
    onShortList,
  ]);

  const className = isFullScreen ? 'boards-container-fullscreen' : 'boards-container-regular';

  if (!selectedBoard || loading) {
    return (
      <Row justify="center" align="middle" style={{ height: '100%', paddingBottom: 125 }}>
        <Spin />
      </Row>
    );
  }
  return (
    <div className={className}>
      {columns}
    </div>
  );
}

BoardView.propTypes = {
  cardTypeId: PropTypes.string.isRequired,
  isLocked: PropTypes.bool.isRequired,
  showCardNumber: PropTypes.bool,
  useCardNumberAsCardTitle: PropTypes.bool,
  userBoardPermissions: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  loading: PropTypes.bool,
  onBoardSelected: PropTypes.func.isRequired,
};

BoardView.defaultProps = {
  showCardNumber: false,
  useCardNumberAsCardTitle: false,
  userBoardPermissions: {},
  loading: false,
};
